import { useEffect, useRef, useState } from "react";
import { ArrowUpRight, Play, RotateCcw } from "lucide-react";
import { BrandLogo } from "../BrandLogo";

const videoUrl = "https://res.myscrm.cn/cdnjs/versions/ai-lab/clawease-mac-app/0.0.0-site-media.20260908/clawease-pms-integration-20260908.mp4";

export function DentalPmsIntegrationVideo() {
  const videoRef = useRef<HTMLVideoElement>(null);
  const playButtonRef = useRef<HTMLButtonElement>(null);
  const [started, setStarted] = useState(false);
  const [ended, setEnded] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (ended && document.activeElement === videoRef.current) {
      playButtonRef.current?.focus({ preventScroll: true });
    }
  }, [ended]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const isDetachedPlayback = () => document.fullscreenElement?.contains(video) || document.pictureInPictureElement === video;
    const observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting && !isDetachedPlayback()) video.pause();
    });
    const pauseWhenHidden = () => {
      if (document.visibilityState === "hidden" && !isDetachedPlayback()) video.pause();
    };
    observer.observe(video);
    document.addEventListener("visibilitychange", pauseWhenHidden);
    return () => {
      observer.disconnect();
      document.removeEventListener("visibilitychange", pauseWhenHidden);
      video.pause();
    };
  }, []);

  const play = async () => {
    const video = videoRef.current;
    if (!video) return;
    if (failed || video.error) {
      setFailed(false);
      video.load();
    }
    if (ended) video.currentTime = 0;
    setEnded(false);
    setStarted(true);
    try {
      await video.play();
      video.focus({ preventScroll: true });
    } catch {
      // Native controls remain available if the browser declines scripted playback.
      if (video.error) setFailed(true);
    }
  };

  return (
    <section id="dental-pms-setup" data-dental-centered-anchor aria-labelledby="dental-pms-setup-title" className="scroll-mt-24 bg-[#fbfaf6] px-5 py-8 sm:px-10 sm:py-10 lg:px-12">
      <div className="dental-content">
        <header className="text-center">
          <h2 id="dental-pms-setup-title" className="mx-auto max-w-[920px] text-balance text-[30px] font-extrabold leading-[1.12] tracking-[-.048em] text-[#172033] sm:text-[38px]">
            One conversation. Your PMS, integrated with ClawEase.
          </h2>
        </header>

        <figure className="relative isolate mx-auto mt-7 max-w-[960px] overflow-hidden rounded-[20px] border border-black bg-black p-1.5 shadow-[0_18px_60px_-24px_rgba(31,35,40,.2)] sm:mt-8 sm:rounded-[26px] sm:p-2">
          <div className="relative overflow-hidden rounded-[15px] bg-black sm:rounded-[19px]">
            <video
              ref={videoRef}
              src={videoUrl}
              aria-label="ClawEase connects to a dental PMS in one conversation"
              aria-describedby="dental-pms-video-caption"
              className="block aspect-[12/7] w-full object-contain focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#9ca3af]"
              controls={started && !ended}
              playsInline
              preload="none"
              tabIndex={started && !ended ? 0 : -1}
              onPlay={() => { setStarted(true); setEnded(false); }}
              onEnded={() => setEnded(true)}
              onError={() => setFailed(true)}
            >
              <a href={videoUrl}>Watch the PMS integration video</a>
            </video>
            {(!started || ended) && !failed && (
              <button ref={playButtonRef} type="button" onClick={play} aria-label={ended ? "Replay PMS integration video" : "Play PMS integration video"} className={`group absolute inset-0 flex flex-col items-center justify-center gap-3 transition-colors duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-white ${ended ? "bg-black/35 backdrop-blur-[2px] hover:bg-black/40" : "bg-black"}`}>
                <span className="flex h-16 w-16 items-center justify-center rounded-full border border-white/35 bg-[#53575e]/55 text-white shadow-[0_8px_28px_rgba(0,0,0,.18)] backdrop-blur-md transition-transform duration-300 group-hover:scale-105 motion-reduce:transform-none sm:h-20 sm:w-20">
                  {ended ? <RotateCcw aria-hidden="true" className="h-6 w-6 sm:h-7 sm:w-7" strokeWidth={1.8} /> : <Play aria-hidden="true" className="ml-1 h-6 w-6 fill-current sm:h-7 sm:w-7" strokeWidth={1.5} />}
                </span>
              </button>
            )}
            {!started && !failed && (
              <div className="pointer-events-none absolute left-4 top-4 sm:left-6 sm:top-5">
                <BrandLogo variant="inverse" size="compact" className="opacity-60 [&_img]:h-5 sm:[&_img]:h-7" />
              </div>
            )}
            {failed && (
              <div role="status" className="absolute inset-0 flex flex-col items-center justify-center gap-4 bg-black/95 p-6 text-center text-sm text-white">
                <p>The video couldn’t load. Please try again.</p>
                <button type="button" onClick={play} className="inline-flex items-center gap-2 rounded-full border border-white/30 bg-white/15 px-5 py-2.5 font-semibold text-white transition-colors hover:bg-white/25 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-white">
                  <RotateCcw aria-hidden="true" className="h-4 w-4" /> Try again
                </button>
                <a href={videoUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 font-semibold text-white no-underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-white">
                  Open video <ArrowUpRight aria-hidden="true" className="h-4 w-4" />
                </a>
              </div>
            )}
          </div>
          <figcaption
            id="dental-pms-video-caption"
            className={!started && !failed
              ? "pointer-events-none absolute inset-x-1.5 bottom-1.5 rounded-b-[15px] border-t border-white/10 bg-white/10 px-4 py-3 text-center text-[12px] leading-relaxed text-white/70 backdrop-blur-md sm:inset-x-2 sm:bottom-2 sm:rounded-b-[19px] sm:px-6 sm:py-4 sm:text-[13px]"
              : "sr-only"}
          >
            See ClawEase explore a PMS, confirm your needs, and build a working booking integration.
          </figcaption>
        </figure>
      </div>
    </section>
  );
}
